import { createContext, useContext, useId, type FC, type ReactNode } from 'react'
import { Icon } from './Icon.js'

export interface FieldContextValue {
  id: string
  invalid: boolean
  describedBy?: string
  required: boolean
}

/**
 * Lets Input, Select and Lookup pick up the label association and the error
 * state without every page having to thread ids by hand.
 */
const FieldContext = createContext<FieldContextValue | null>(null)

export const useFieldContext = (): FieldContextValue | null => useContext(FieldContext)

export interface FieldProps {
  label: string
  required?: boolean
  hint?: string
  error?: string
  children?: ReactNode
}

export const Field: FC<FieldProps> = ({ label, required = false, hint, error, children }) => {
  const base = useId()
  const id = `${base}-control`
  const hintId = `${base}-hint`
  const errorId = `${base}-error`
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ')

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        <span>{label}</span>
        {/* Spelled out: a lone asterisk is invisible to a screen reader and to print. */}
        {required ? <span className="field__req">Required</span> : null}
      </label>

      <FieldContext.Provider
        value={{ id, invalid: Boolean(error), describedBy: describedBy || undefined, required }}
      >
        {children}
      </FieldContext.Provider>

      {hint ? (
        <div className="field__hint" id={hintId}>
          {hint}
        </div>
      ) : null}

      {error ? (
        <div className="field__error" id={errorId}>
          <Icon name="warn" size={16} />
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  )
}
